import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { generatePath, Link as RouterLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Box, Chip, Link, Typography } from '@mui/material'
import LoadingPage from '../../pages/LoadingPage'
import { SisSyncManager } from '../../APIManager'
import { notifyError } from '../../utils/notifyError'
import ROUTES from '../../consts/paths'
import { type ApplyFiltersAndDispatchParams, type Row } from '../../components/CustomTable/types'
import { type DataKey } from '../../models/DataObject'
import { type ISisSyncRun, type ISisSyncRunListResponse } from '../../models/SisSyncRun'
import { SisSyncRunsFilters } from './SisSyncRunsFilters'
import { formatRunListTimestamp, getRunStatusChip, getRunTriggerLabel } from '../../helpers/sisSyncRunDisplay'
import { buildRunFilters, EMPTY_FILTERS, getRunFilesSummary, getRunTooltipLines, type SisSyncRunFilterValues } from './helpers'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

// The backend's list keys have no file counts, so the page adds this column. It can't sort: the
// backend rejects a sort on a field outside its keys (allowSort left unset reads as false).
const FILES_COLUMN: DataKey = { field: 'files_total', headerName: 'Files', type: 'Text', shown: true, alwaysShow: false }

const runDetailPath = (runId: string): string => generatePath(ROUTES.SIS_SYNC_RUN_DETAILS, { runId })

export const SisSyncLogsFeature = (): JSX.Element => {
  const navigate = useNavigate()
  // Kept out of `params`: CustomTable mirrors `params` into the URL on sort, and this page does not
  // read filters back from the URL, so a mirrored filter would look shareable without applying.
  const [filterValues, setFilterValues] = useState<SisSyncRunFilterValues>(EMPTY_FILTERS)
  const filters = buildRunFilters(filterValues)
  // No sort sent until a header is clicked: the backend default is newest first.
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({
    pageSize: 20,
    pageNumber: 1
  })

  const { data: runList, isLoading, isError, error } = useQuery({
    queryKey: ['sisSyncRuns', params, filters],
    queryFn: async (): Promise<ISisSyncRunListResponse> => {
      const result = await SisSyncManager.getSisSyncRuns({
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        filters,
        sort: typeof params.sort === 'string' ? params.sort : undefined
      })
      return result.data
    },
    // The page shows its own error message; without this the global query error toast fires too.
    meta: { silent: true }
  })

  useEffect(() => {
    if (isError) notifyError(error, { fallbackMessage: 'Failed to load SIS sync runs. Please try again.' })
  }, [isError])

  const columns = useMemo(() => (runList?.keys != null ? [...runList.keys, FILES_COLUMN] : []), [runList?.keys])

  const handleFiltersChange = (values: SisSyncRunFilterValues): void => {
    setFilterValues(values)
    setParams((prev) => ({ ...prev, pageNumber: 1 }))
  }

  const overrides = {
    startedOn: (run: ISisSyncRun) => (
      <Link
        component={RouterLink}
        to={runDetailPath(run.sisSyncRunId)}
        // The row click navigates too; stop it so one click is one navigation.
        onClick={(event: React.MouseEvent) => { event.stopPropagation() }}
      >
        {formatRunListTimestamp(run.startedOn)}
      </Link>
    ),
    finishedOn: (run: ISisSyncRun) => <Typography>{formatRunListTimestamp(run.finishedOn)}</Typography>,
    trigger: (run: ISisSyncRun) => <Typography>{getRunTriggerLabel(run.trigger)}</Typography>,
    outcome: (run: ISisSyncRun) => {
      const { label, color, variant } = getRunStatusChip(run, { withProgress: true })
      return <Chip label={label} size='small' color={color} variant={variant} />
    },
    filesTotal: (run: ISisSyncRun) => <Typography>{getRunFilesSummary(run)}</Typography>
  }

  const getRowTooltip = (rowData: Row): React.ReactNode => {
    const lines = getRunTooltipLines(rowData as Partial<ISisSyncRun>)
    if (lines.length === 0) return null
    return (
      <Box>
        {lines.map((line, index) => (
          <Typography key={index} variant='body2'>{line}</Typography>
        ))}
      </Box>
    )
  }

  return (
    <Suspense fallback={<LoadingPage />}>
      <AppTable
        // The page header already says "SIS Sync Logs", which is also the endpoint's header name.
        topbarTitle='Sync runs'
        list={runList}
        rows={runList?.data ?? []}
        columns={columns}
        loading={isLoading}
        overrides={overrides}
        featureName='SIS Sync Logs'
        hideActionsColumn={true}
        disableRowDoubleClick={true}
        hideSearch={true}
        getRowTooltip={getRowTooltip}
        clickableRows={true}
        hasActiveFilters={filters !== undefined}
        onRowClick={(row: Row) => {
          const runId = row?.row?.data?.sisSyncRunId
          if (runId) navigate(runDetailPath(runId))
        }}
        customTopbarAction={<SisSyncRunsFilters values={filterValues} onChange={handleFiltersChange} />}
        params={params}
        setParams={setParams}
      />
    </Suspense>
  )
}
